import type {
  DsdEditorCompanyNameMatchedBlock,
  DsdEditorCompanyNameReference,
  DsdEditorParagraphSegment,
} from "@/apis/dsd-editor/route";
import { matchedParagraphKey } from "@/utils/dsd-editor/matched-paragraphs";

/** 문단 블록 → chunkId+정규화텍스트 키 세그먼트 맵 (매칭 문단과 동일한 대치 렌더 방식) */
export const buildCompanyNameParagraphSegments = (
  blocks: DsdEditorCompanyNameMatchedBlock[],
): ReadonlyMap<string, DsdEditorParagraphSegment[]> => {
  const map = new Map<string, DsdEditorParagraphSegment[]>();

  for (const block of blocks) {
    if (block.blockType !== "paragraph") continue;

    const text = block.segments.map((segment) => segment.text).join("");
    map.set(matchedParagraphKey(block.chunkId, text), block.segments);
  }

  return map;
};

/** 표 셀 블록 → cellId(valueItemRef) 키 세그먼트 맵 (셀 값 대치 렌더) */
export const buildCompanyNameCellSegments = (
  blocks: DsdEditorCompanyNameMatchedBlock[],
): ReadonlyMap<string, DsdEditorParagraphSegment[]> => {
  const map = new Map<string, DsdEditorParagraphSegment[]>();

  for (const block of blocks) {
    if (block.blockType === "paragraph") continue;
    map.set(block.valueItemRef, block.segments);
  }

  return map;
};

/** 확인 필요 표기의 occurrenceId 집합 — 본문 하이라이트 색(노랑/초록) 구분용 */
export const getCompanyNameCautionOccurrenceIds = (
  references: DsdEditorCompanyNameReference[],
): ReadonlySet<string> => {
  const ids = new Set<string>();

  for (const reference of references) {
    for (const occurrence of reference.occurrences) {
      if (occurrence.needsUnification) ids.add(occurrence.occurrenceId);
    }
  }

  return ids;
};

/** 확인 필요 표기가 들어 있는 청크 — 목차 점 배지용 */
export const getCompanyNameCautionChunkIds = (
  blocks: DsdEditorCompanyNameMatchedBlock[],
  cautionOccurrenceIds: ReadonlySet<string>,
): ReadonlySet<string> =>
  new Set(
    blocks
      .filter((block) =>
        block.segments.some(
          (segment) =>
            !!segment.occurrenceId &&
            cautionOccurrenceIds.has(segment.occurrenceId),
        ),
      )
      .map((block) => block.chunkId),
  );

/** occurrenceId → 위치 라벨(청크 제목, 없으면 chunkId) — 패널 카드 캡션용 */
export const buildCompanyNameOccurrenceLocations = (
  blocks: DsdEditorCompanyNameMatchedBlock[],
  chunkTitleById: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> => {
  const map = new Map<string, string>();

  for (const block of blocks) {
    const label = chunkTitleById.get(block.chunkId) || block.chunkId;
    for (const segment of block.segments) {
      if (segment.occurrenceId) map.set(segment.occurrenceId, label);
    }
  }

  return map;
};

/** occurrenceId → 대표 표기 — 본문 클릭 시 해당 대표명 상세로 진입 */
export const buildCompanyNameRepresentativeByOccurrenceId = (
  references: DsdEditorCompanyNameReference[],
): ReadonlyMap<string, string> => {
  const map = new Map<string, string>();

  for (const reference of references) {
    for (const occurrence of reference.occurrences) {
      map.set(occurrence.occurrenceId, reference.representative);
    }
  }

  return map;
};

/** 확인 필요 표기 개수 — 탭 카운트 */
export const countCompanyNamesNeedingUnification = (
  references: DsdEditorCompanyNameReference[],
): number =>
  references.reduce(
    (sum, reference) =>
      sum +
      reference.occurrences.filter((occurrence) => occurrence.needsUnification)
        .length,
    0,
  );
